<template>
  <div class="tch-ranking-page">
    <div ref="exportArea" class="export-sheet">
    <div class="hero">
      <div class="hero-glow hero-glow-a"></div>
      <div class="hero-glow hero-glow-b"></div>
      <div class="hero-main">
        <div class="hero-title-wrap">
          <div class="hero-kicker">HELLOKID · TEACHER HALL OF FAME</div>
          <h1 class="hero-title">优秀外教排行榜</h1>
          <p class="hero-sub">老老师：在职试用或正式、统计期内有课，结束日当周买断 ≥ 20 节才入榜，退费学生 > 3 或返课时 > 8 出榜不占名次</p>
          <p class="hero-sub">新老师：开课 30 天内、统计期内有课可入新锐榜；结束日当周买断 &lt; 20 进未入榜；退费/返课<span class="hero-sub-keep">不出榜</span></p>
        </div>
        <div class="hero-stats">
          <div class="hero-stat" v-for="item in heroStats" :key="item.label">
            <div class="hero-stat-value">{{ item.value }}</div>
            <div class="hero-stat-label">{{ item.label }}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="rank-toolbar export-skip">
      <div class="seg">
        <a v-for="item in periodOptions" :key="item.value"
           href="javascript:;"
           class="seg-item"
           :class="{'is-active': periodType === item.value}"
           @click="onPeriodTypeChange(item.value)">{{ item.label }}</a>
      </div>
      <div class="tool-field">
        <span class="tool-label">日期</span>
        <Date-picker
          class="range-picker"
          type="daterange"
          format="yyyy-MM-dd"
          value-format="yyyy-MM-dd"
          v-model="dateRange"
          :clearable="false"
          transfer
          placeholder="选择日期范围"
          @on-change="onDateRangeChange">
        </Date-picker>
      </div>
      <div class="tool-field">
        <span class="tool-label">外教运营</span>
        <Select v-model.number="studyAdviserId" placeholder="全部"
                class="adviser-select"
                clearable
                @on-change="onFilterChange">
          <Option v-for="item in studyAdviserList"
                  :value="item.id" :key="item.id">{{ item.nickname }}
          </Option>
        </Select>
      </div>
      <div class="tool-field">
        <span class="tool-label">开课时间</span>
        <Date-picker
          class="range-picker"
          type="daterange"
          format="yyyy-MM-dd"
          value-format="yyyy-MM-dd"
          v-model="openCourseRange"
          transfer
          placeholder="不限"
          @on-change="onFilterChange">
        </Date-picker>
      </div>
      <div class="tool-field">
        <span class="tool-label">老师性别</span>
        <Select v-model.number="sex" placeholder="全部"
                class="sex-select"
                clearable
                @on-change="onFilterChange">
          <Option value="1">男</Option>
          <Option value="2">女</Option>
        </Select>
      </div>
      <div class="tool-field">
        <span class="tool-label">榜单</span>
        <Select v-model="boardFilter" placeholder="全部"
                class="sex-select"
                clearable>
          <Option v-for="item in boardFilterOptions"
                  :value="item.value" :key="item.value">{{ item.label }}
          </Option>
        </Select>
      </div>
      <div class="tool-actions">
        <a class="rule-link" href="javascript:;" :class="{'is-busy': exportLoading}" @click="exportToImage">
          <Icon type="ios-download-outline"></Icon> {{ exportLoading ? '导出中' : '导出图片' }}
        </a>
        <a class="rule-link" href="javascript:;" @click="ruleModal = true">
          <Icon type="ios-information-outline"></Icon> 计分规则
        </a>
      </div>
    </div>

    <Spin size="large" fix v-if="pageLoading"></Spin>

    <div class="stage" v-if="showPodium && podium.length">
      <div class="stage-beam stage-beam-l"></div>
      <div class="stage-beam stage-beam-c"></div>
      <div class="stage-beam stage-beam-r"></div>
      <div class="stage-title">TOP 3 · 本期之星</div>
      <div class="podium">
        <div v-for="(row, idx) in podium" :key="row.tchId"
             class="podium-col" :class="'is-rank-' + row.rank"
             :style="{animationDelay: (idx * 0.12) + 's'}">
          <div class="podium-person">
            <div class="crown" v-if="row.rank === 1">♛</div>
            <div class="avatar" :class="medalClass(row.rank)">
              <span>{{ initials(row.tchName) }}</span>
            </div>
            <div class="medal medal-podium" :class="medalClass(row.rank)">
              <i class="medal-ribbon medal-ribbon-l"></i>
              <i class="medal-ribbon medal-ribbon-r"></i>
              <span class="medal-core">{{ row.rank }}</span>
            </div>
            <a href="javascript:;" class="podium-name" :title="(row.tchName || '老师') + ' 详情'" @click="openTchInfo(row)">{{ row.tchName || '—' }}</a>
            <div class="podium-id">ID {{ row.tchId }} · {{ row.studyAdviserName || '未分配运营' }}</div>
            <div class="podium-score">
              {{ formatScore(row.totalScore) }}<span class="podium-score-unit">分</span>
            </div>
            <div class="podium-bars">
              <div class="pbar" v-for="metric in podiumMetrics" :key="metric.value">
                <span class="pbar-label">{{ metric.title }}</span>
                <span class="pbar-track">
                  <i class="pbar-fill" :class="gradeLevel(row[metric.grade])"
                     :style="{width: gradeWidth(row[metric.grade])}"></i>
                </span>
                <span class="pbar-value">{{ displayValue(row[metric.value]) }}</span>
              </div>
            </div>
          </div>
          <div class="pedestal">
            <span class="pedestal-num">{{ row.rank }}</span>
            <span class="pedestal-tag">{{ rankTitle(row.rank) }}</span>
          </div>
        </div>
      </div>
    </div>

    <template v-if="showChallengers">
    <!-- 1 挑战者 formal ranked -->
    <div class="board-head">
      <h2 class="board-title">挑战者</h2>
      <span class="board-legend">
        正式有名次（领奖台为 TOP3）；指标色带：
        <i class="legend-dot is-healthy"></i>A/B
        <i class="legend-dot is-warning"></i>C
        <i class="legend-dot is-danger"></i>D/E
      </span>
    </div>
    <div class="card-grid" v-if="challengers.length">
      <div v-for="(row, idx) in challengers" :key="'c-' + row.tchId"
           class="rank-card" :class="scoreLevel(row.totalScore)"
           :style="{animationDelay: Math.min(idx, 12) * 0.04 + 's'}">
        <div class="rank-card-top">
          <div class="medal medal-sm" :class="medalClass(row.rank)">
            <i class="medal-ribbon medal-ribbon-l"></i>
            <i class="medal-ribbon medal-ribbon-r"></i>
            <span class="medal-core">{{ row.rank }}</span>
          </div>
          <a href="javascript:;"
             class="rank-avatar"
             :title="(row.tchName || '老师') + ' 详情'"
             @click="openTchInfo(row)">
            <span>{{ initials(row.tchName) }}</span>
          </a>
          <div class="rank-card-who">
            <a href="javascript:;" class="rank-card-name" :title="(row.tchName || '老师') + ' 详情'" @click="openTchInfo(row)">
              {{ row.tchName || '—' }}
              <span class="top10" v-if="row.rank <= 10">TOP 10</span>
            </a>
            <div class="rank-card-sub">ID {{ row.tchId }} · {{ row.studyAdviserName || '未分配运营' }}</div>
            <div class="rank-card-sub">开课 {{ formatOpenCourse(row.openCourseTime) }} · 有课 {{ row.lessonCount || 0 }} 节</div>
          </div>
          <div class="score-ring" :style="ringStyle(row.totalScore)">
            <div class="score-ring-inner">
              <div class="score-ring-num">{{ formatScoreShort(row.totalScore) }}</div>
              <div class="score-ring-unit">分</div>
            </div>
          </div>
        </div>
        <div class="strip">
          <span v-for="metric in metrics" :key="metric.value"
                class="strip-seg" :class="gradeLevel(row[metric.grade])"
                :style="{flex: metric.weight}"
                :title="metricTip(row, metric)"></span>
        </div>
        <div class="strip-caption">
          <span>10 项指标（宽度 = 权重）</span>
          <a href="javascript:;" class="toggle" @click="toggle(row.tchId)">
            {{ expanded[row.tchId] ? '收起' : '明细' }}
            <Icon :type="expanded[row.tchId] ? 'ios-arrow-up' : 'ios-arrow-down'"></Icon>
          </a>
        </div>
        <div class="metric-grid" v-if="expanded[row.tchId]">
          <div class="metric-chip" v-for="metric in metrics" :key="metric.value"
               :title="metricTip(row, metric)">
            <div class="metric-chip-label">{{ metric.title }}<em>{{ metric.weight }}</em></div>
            <div class="metric-chip-value">{{ displayValue(row[metric.value]) }}</div>
            <div class="metric-chip-grade" :class="gradeLevel(row[metric.grade])">
              {{ gradeText(row[metric.grade]) }} · {{ formatScore(row[metric.score]) }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="block-empty" v-else-if="!frLoading">暂无挑战者</div>
    <div class="pager" v-if="frTotal > frPageSize">
      <Page :total="frTotal" :current="frPageIndex" :page-size="frPageSize" show-total @on-change="onFrPageChange"></Page>
    </div>
    </template>

    <template v-if="showFormalUnranked">
    <!-- 2 挑战者未入榜 formal unranked（含出榜） -->
    <div class="board-head">
      <h2 class="board-title is-warn">{{ formalUnrankedTitle }}</h2>
      <span class="board-legend">{{ formalUnrankedLegend }}</span>
    </div>
    <div class="card-grid" v-if="fuRows.length">
      <div v-for="row in fuRows" :key="'fu-' + row.tchId" class="rank-card is-out is-unranked">
        <div class="rank-card-top">
          <div class="out-badge" :class="{'is-unranked': !isOutOnly(row)}">{{ unrankedBadge(row) }}</div>
          <div class="rank-card-who">
            <a href="javascript:;" class="rank-card-name" :title="(row.tchName || '老师') + ' 详情'" @click="openTchInfo(row)">{{ row.tchName || '—' }}</a>
            <div class="rank-card-sub">ID {{ row.tchId }} · {{ row.studyAdviserName || '未分配运营' }}</div>
            <div class="out-reason">{{ unrankedReasonText(row) }}</div>
          </div>
          <div class="out-score">
            <div class="out-score-num">{{ formatScore(row.totalScore) }}</div>
            <div class="out-score-label">计算分</div>
          </div>
        </div>
        <div class="strip">
          <span v-for="metric in metrics" :key="metric.value"
                class="strip-seg" :class="gradeLevel(row[metric.grade])"
                :style="{flex: metric.weight}"
                :title="metricTip(row, metric)"></span>
        </div>
      </div>
    </div>
    <div class="block-empty" v-else-if="!fuLoading">
      <div>{{ formalUnrankedEmpty }}</div>
      <div v-if="boardFilter === 'out'" class="block-empty-hint">可翻页或调整统计周期后再试</div>
    </div>
    <div class="pager" v-if="fuTotal > fuPageSize">
      <Page :total="fuTotal" :current="fuPageIndex" :page-size="fuPageSize" show-total @on-change="onFuPageChange"></Page>
    </div>
    </template>

    <template v-if="showRookie">
    <!-- 3 新锐榜 rookie ranked -->
    <div class="rookie-box">
      <div class="board-head">
        <h2 class="board-title is-rookie">新锐榜</h2>
        <span class="board-legend">开课 30 天内有名次老师；退费/返课不出榜</span>
      </div>
      <div class="card-grid" v-if="rrRows.length">
        <div v-for="(row, idx) in rrRows" :key="'rr-' + row.tchId"
             class="rank-card is-rookie" :class="scoreLevel(row.totalScore)"
             :style="{animationDelay: Math.min(idx, 12) * 0.04 + 's'}">
          <div class="rank-card-top">
            <div class="medal medal-sm medal-rookie">
              <span class="medal-core">新{{ row.rank || (idx + 1) }}</span>
            </div>
            <a href="javascript:;"
               class="rank-avatar is-rookie"
               :title="(row.tchName || '老师') + ' 详情'"
               @click="openTchInfo(row)">
              <span>{{ initials(row.tchName) }}</span>
            </a>
            <div class="rank-card-who">
              <a href="javascript:;" class="rank-card-name" :title="(row.tchName || '老师') + ' 详情'" @click="openTchInfo(row)">
                {{ row.tchName || '—' }}
                <span class="rookie-tag">新锐</span>
              </a>
              <div class="rank-card-sub">ID {{ row.tchId }} · {{ row.studyAdviserName || '未分配运营' }}</div>
              <div class="rank-card-sub">开课 {{ formatOpenCourse(row.openCourseTime) }} · 有课 {{ row.lessonCount || 0 }} 节</div>
            </div>
            <div class="score-ring" :style="ringStyle(row.totalScore)">
              <div class="score-ring-inner">
                <div class="score-ring-num">{{ formatScoreShort(row.totalScore) }}</div>
                <div class="score-ring-unit">分</div>
              </div>
            </div>
          </div>
          <div class="strip">
            <span v-for="metric in rookieMetrics" :key="metric.value"
                  class="strip-seg" :class="gradeLevel(row[metric.grade])"
                  :style="{flex: metric.weight}"
                  :title="metricTip(row, metric)"></span>
          </div>
          <div class="strip-caption">
            <span>10 项指标（新锐权重，宽度 = 权重）</span>
            <a href="javascript:;" class="toggle" @click="toggleRookie(row.tchId)">
              {{ rookieExpanded[row.tchId] ? '收起' : '明细' }}
              <Icon :type="rookieExpanded[row.tchId] ? 'ios-arrow-up' : 'ios-arrow-down'"></Icon>
            </a>
          </div>
          <div class="metric-grid" v-if="rookieExpanded[row.tchId]">
            <div class="metric-chip" v-for="metric in rookieMetrics" :key="metric.value"
                 :title="metricTip(row, metric)">
              <div class="metric-chip-label">{{ metric.title }}<em>{{ metric.weight }}</em></div>
              <div class="metric-chip-value">{{ displayValue(row[metric.value]) }}</div>
              <div class="metric-chip-grade" :class="gradeLevel(row[metric.grade])">
                {{ gradeText(row[metric.grade]) }} · {{ formatScore(row[metric.score]) }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="block-empty" v-else-if="!rrLoading">暂无新锐老师</div>
      <div class="pager" v-if="rrTotal > rrPageSize">
        <Page :total="rrTotal" :current="rrPageIndex" :page-size="rrPageSize" show-total @on-change="onRrPageChange"></Page>
      </div>
    </div>
    </template>

    <template v-if="showRookieUnranked">
    <!-- 4 新锐榜未入榜 rookie unranked -->
    <div class="board-head">
      <h2 class="board-title is-warn is-rookie-unranked">新锐榜未入榜</h2>
      <span class="board-legend">结束日当周买断&lt;20 不入新锐有名次；名单随上方统计周期变化</span>
    </div>
    <div class="card-grid" v-if="ruRows.length">
      <div v-for="row in ruRows" :key="'ru-' + row.tchId" class="rank-card is-out is-unranked is-rookie">
        <div class="rank-card-top">
          <div class="out-badge is-unranked">未入榜</div>
          <div class="rank-card-who">
            <a href="javascript:;" class="rank-card-name" :title="(row.tchName || '老师') + ' 详情'" @click="openTchInfo(row)">{{ row.tchName || '—' }}</a>
            <div class="rank-card-sub">ID {{ row.tchId }} · {{ row.studyAdviserName || '未分配运营' }}</div>
            <div class="out-reason">{{ unrankedReasonText(row) }}</div>
          </div>
          <div class="out-score">
            <div class="out-score-num">{{ formatScore(row.totalScore) }}</div>
            <div class="out-score-label">计算分</div>
          </div>
        </div>
        <div class="strip">
          <span v-for="metric in rookieMetrics" :key="metric.value"
                class="strip-seg" :class="gradeLevel(row[metric.grade])"
                :style="{flex: metric.weight}"
                :title="metricTip(row, metric)"></span>
        </div>
      </div>
    </div>
    <div class="block-empty" v-else-if="!ruLoading">暂无新锐未入榜老师</div>
    <div class="pager" v-if="ruTotal > ruPageSize">
      <Page :total="ruTotal" :current="ruPageIndex" :page-size="ruPageSize" show-total @on-change="onRuPageChange"></Page>
    </div>
    </template>

    <div class="empty" v-if="showEmpty">
      <div class="empty-trophy">🏆</div>
      <div class="empty-title">本期暂无参评老师</div>
      <div class="empty-sub">可换个统计周期或放宽筛选后再看四块榜单。</div>
    </div>

    </div>

        <tch-info-modal :value="showTchInfoModal" @on-close="showTchInfoModal = false" :tchId="tchId"></tch-info-modal>

    <Modal v-model="ruleModal" title="计分规则" width="860" footer-hide>
      <p class="rule-head">单项得分 = 权重 × 档位系数（A 1.0 / B 0.8 / C 0.6 / D 0.3 / E 0），满分 100。</p>
      <div class="rule-title">老老师（正式榜）</div>
      <table class="rule-table">
        <thead>
          <tr>
            <th>指标</th>
            <th>方向</th>
            <th>权重</th>
            <th>A</th>
            <th>B</th>
            <th>C</th>
            <th>D</th>
            <th>E</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in ruleRows" :key="item.name">
            <td>{{ item.name }}</td>
            <td>{{ item.dir }}</td>
            <td>{{ item.weight }}</td>
            <td v-for="(grade, idx) in item.grades" :key="idx">{{ grade }}</td>
          </tr>
        </tbody>
      </table>
      <p class="rule-foot">比较用未四舍五入的原始比值；星级无已评分课节按 B 档；Demo 出席成交率 ≥20% 为 A，其余（含无 Demo 出席）为 B；请假、临时请假、迟到、早退无分母按 A 档；全勤率、上课率无分母按 E 档。同分按有课课节数多者在前，再按老师 ID 升序。入榜门槛只对老老师生效：查询结束日所在自然周的买断课时 ≥ 20 节，与上课率里的买断课时同一算法。</p>
      <div class="rule-title is-rookie">新锐榜（开课 30 天内）</div>
      <table class="rule-table">
        <thead>
          <tr>
            <th>指标</th>
            <th>方向</th>
            <th>权重</th>
            <th>A</th>
            <th>B</th>
            <th>C</th>
            <th>D</th>
            <th>E</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in rookieRuleRows" :key="item.name">
            <td>{{ item.name }}</td>
            <td>{{ item.dir }}</td>
            <td>{{ item.weight }}</td>
            <td v-for="(grade, idx) in item.grades" :key="idx">{{ grade }}</td>
          </tr>
        </tbody>
      </table>
      <p class="rule-foot">开课时间在查询结束日往前 30 天内（含）的老师进新锐榜，不进正式榜。星级无已评分课节按 B 档；全勤率、上课率无数据按 A 档；请假、临时请假、迟到、早退、退费、返课未发生按 A 档。只用五档；退费 &gt;3 人、返课 &gt;8 记 E 档得 0、不出榜；结束日当周买断 &lt; 20 进「新锐榜未入榜」。</p>
    </Modal>
  </div>
</template>

<script>
import html2canvas from 'html2canvas'
import TchInfoModal from './modal/TchInfoModal.vue'

const ALL_START_DATE = '2018-01-01'

const METRICS = [
  {title: '全勤率', weight: 20, value: 'fullAttendanceRate', grade: 'fullAttendanceGrade', score: 'fullAttendanceScore'},
  {title: '上课率', weight: 30, value: 'attendLessonRate', grade: 'attendLessonGrade', score: 'attendLessonScore'},
  {title: '星级评价', weight: 15, value: 'starRating', grade: 'starGrade', score: 'starScore', extra: 'ratedCount', extraLabel: '已评分节数'},
  {title: '请假率', weight: 10, value: 'leaveRate', grade: 'leaveGrade', score: 'leaveScore'},
  {title: '临时请假率', weight: 6, value: 'tempLeaveRate', grade: 'tempLeaveGrade', score: 'tempLeaveScore'},
  {title: '迟到率', weight: 4, value: 'lateRate', grade: 'lateGrade', score: 'lateScore'},
  {title: '早退率', weight: 4, value: 'earlyRate', grade: 'earlyGrade', score: 'earlyScore'},
  {title: '退费学生数', weight: 6, value: 'refundStudentCount', grade: 'refundGrade', score: 'refundScore'},
  {title: '返课时数', weight: 3, value: 'returnLessonHours', grade: 'returnLessonGrade', score: 'returnLessonScore'},
  {title: 'Demo成交率', weight: 2, value: 'demoAttendDealRate', grade: 'demoGrade', score: 'demoScore', extra: 'demoAttendCount', extraLabel: 'Demo出席学生数'}
]

const ROOKIE_METRICS = [
  {title: '星级评价', weight: 60, value: 'starRating', grade: 'starGrade', score: 'starScore', extra: 'ratedCount', extraLabel: '已评分节数'},
  {title: '全勤率', weight: 6, value: 'fullAttendanceRate', grade: 'fullAttendanceGrade', score: 'fullAttendanceScore'},
  {title: '上课率', weight: 4, value: 'attendLessonRate', grade: 'attendLessonGrade', score: 'attendLessonScore'},
  {title: '请假率', weight: 6, value: 'leaveRate', grade: 'leaveGrade', score: 'leaveScore'},
  {title: '临时请假率', weight: 5, value: 'tempLeaveRate', grade: 'tempLeaveGrade', score: 'tempLeaveScore'},
  {title: '迟到率', weight: 4, value: 'lateRate', grade: 'lateGrade', score: 'lateScore'},
  {title: '早退率', weight: 4, value: 'earlyRate', grade: 'earlyGrade', score: 'earlyScore'},
  {title: '退费学生数', weight: 6, value: 'refundStudentCount', grade: 'refundGrade', score: 'refundScore'},
  {title: '返课时数', weight: 3, value: 'returnLessonHours', grade: 'returnLessonGrade', score: 'returnLessonScore'},
  {title: 'Demo成交率', weight: 2, value: 'demoAttendDealRate', grade: 'demoGrade', score: 'demoScore', extra: 'demoAttendCount', extraLabel: 'Demo出席学生数'}
]

const GRADE_WIDTH = {A: '100%', B: '80%', C: '60%', D: '30%', E: '6%', OUT: '6%'}

function pad (n) {
  return n < 10 ? '0' + n : '' + n
}

function formatDate (date) {
  return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate())
}

function isBlank (val) {
  return val === null || val === undefined || val === ''
}

function toDateText (val) {
  if (val instanceof Date) {
    return isNaN(val.getTime()) ? '' : formatDate(val)
  }
  return isBlank(val) ? '' : String(val).slice(0, 10)
}

export default {
  name: 'TchTeacherRanking',
  components: {
    TchInfoModal
  },
  data () {
    return {
      periodOptions: [
        {label: '日', value: 'day'},
        {label: '周', value: 'week'},
        {label: '月', value: 'month'},
        {label: '年', value: 'year'},
        {label: '全部', value: 'all'}
      ],
      metrics: METRICS,
      rookieMetrics: ROOKIE_METRICS,
      podiumMetrics: METRICS.slice(0, 3),
      periodType: 'month',
      dateRange: [],
      studyAdviserId: null,
      sex: null,
      boardFilter: '',
      boardFilterOptions: [
        {label: '老老师', value: 'veteran'},
        {label: '新锐榜', value: 'rookie'},
        {label: '出榜老师', value: 'out'}
      ],
      studyAdviserList: [],
      openCourseRange: [],
      exportLoading: false,
      champion: null,
      podiumCache: [],
      expanded: {},
      rookieExpanded: {},
      frPageIndex: 1,
      frPageSize: 20,
      frTotal: 0,
      frData: [],
      frLoading: false,
      frSeq: 0,
      fuPageIndex: 1,
      fuPageSize: 20,
      fuTotal: 0,
      fuData: [],
      fuLoading: false,
      fuSeq: 0,
      rrPageIndex: 1,
      rrPageSize: 20,
      rrTotal: 0,
      rrData: [],
      rrLoading: false,
      rrSeq: 0,
      ruPageIndex: 1,
      ruPageSize: 20,
      ruTotal: 0,
      ruData: [],
      ruLoading: false,
      ruSeq: 0,
      showTchInfoModal: false,
      tchId: null,
      ruleModal: false,
      ruleRows: [
        {name: '上课率', dir: '正', weight: 30, grades: ['≥95%', '≥85%', '≥70%', '≥50%', '<50%']},
        {name: '全勤率', dir: '正', weight: 20, grades: ['≥95%', '≥85%', '≥70%', '≥50%', '<50%']},
        {name: '星级评价', dir: '正', weight: 15, grades: ['≥4.50', '≥4.00', '≥3.50', '≥3.00', '<3.00']},
        {name: '请假率', dir: '反', weight: 10, grades: ['0', '≤1.5%', '≤2.5%', '≤4%', '>4%']},
        {name: '临时请假率', dir: '反', weight: 6, grades: ['0', '≤0.6%', '≤1.2%', '≤2%', '>2%']},
        {name: '迟到率', dir: '反', weight: 4, grades: ['0', '≤1%', '≤3%', '≤5%', '>5%']},
        {name: '早退率', dir: '反', weight: 4, grades: ['0', '≤1%', '≤3%', '≤5%', '>5%']},
        {name: '退费学生数', dir: '反', weight: 6, grades: ['0 人', '1 人', '2 人', '3 人', '>3 人出榜']},
        {name: '返课时数', dir: '反', weight: 3, grades: ['0', '≤3', '≤5', '≤8', '>8 出榜']},
        {name: 'Demo 出席成交率', dir: '正', weight: 2, grades: ['≥20%', '其余（含无 Demo）', '—', '—', '—']}
      ],
      rookieRuleRows: [
        {name: '星级评价', dir: '正', weight: 60, grades: ['≥4.50', '≥4.00', '≥3.50', '≥3.00', '<3.00']},
        {name: '全勤率', dir: '正', weight: 6, grades: ['≥95%', '≥85%', '≥70%', '≥50%', '<50%']},
        {name: '上课率', dir: '正', weight: 4, grades: ['≥95%', '≥85%', '≥70%', '≥50%', '<50%']},
        {name: '请假率', dir: '反', weight: 6, grades: ['0', '≤1.5%', '≤2.5%', '≤4%', '>4%']},
        {name: '临时请假率', dir: '反', weight: 5, grades: ['0', '≤0.6%', '≤1.2%', '≤2%', '>2%']},
        {name: '迟到率', dir: '反', weight: 4, grades: ['0', '≤1%', '≤3%', '≤5%', '>5%']},
        {name: '早退率', dir: '反', weight: 4, grades: ['0', '≤1%', '≤3%', '≤5%', '>5%']},
        {name: '退费学生数', dir: '反', weight: 6, grades: ['0 人', '1 人', '2 人', '3 人', '>3 人（不出榜）']},
        {name: '返课时数', dir: '反', weight: 3, grades: ['0', '≤3', '≤5', '≤8', '>8（不出榜）']},
        {name: 'Demo 出席成交率', dir: '正', weight: 2, grades: ['≥20%', '≥10%', '其余（含无 Demo）', '—', '—']}
      ]
    }
  },
  computed: {
    pageLoading () {
      return this.frLoading || this.fuLoading || this.rrLoading || this.ruLoading
    },
    showPodium () {
      return !this.boardFilter
    },
    showChallengers () {
      return !this.boardFilter || this.boardFilter === 'veteran'
    },
    showFormalUnranked () {
      return !this.boardFilter || this.boardFilter === 'out'
    },
    showRookie () {
      return !this.boardFilter || this.boardFilter === 'rookie'
    },
    showRookieUnranked () {
      return !this.boardFilter
    },
    formalUnrankedTitle () {
      return this.boardFilter === 'out' ? '出榜老师' : '挑战者未入榜'
    },
    formalUnrankedLegend () {
      if (this.boardFilter === 'out') {
        return '退费学生数 > 3 或返课时数 > 8；名单随上方统计周期变化'
      }
      return '结束日当周买断<20，或退费学生数>3 / 返课时数>8；名单随上方统计周期变化'
    },
    formalUnrankedEmpty () {
      return this.boardFilter === 'out' ? '暂无出榜老师' : '暂无未入榜老师'
    },
    podium () {
      const top = (this.podiumCache || []).filter(row => Number(row.rank) <= 3)
      const byRank = {}
      top.forEach(row => { byRank[row.rank] = row })
      return [byRank[2], byRank[1], byRank[3]].filter(Boolean)
    },
    challengers () {
      return (this.frData || []).filter(row => !isBlank(row.rank) && Number(row.rank) > 3)
    },
    fuRows () {
      const rows = this.fuData || []
      if (this.boardFilter === 'out') {
        return rows.filter(row => !isBlank(row.outReason))
      }
      return rows
    },
    rrRows () {
      return this.rrData || []
    },
    ruRows () {
      return this.ruData || []
    },
    heroStats () {
      return [
        {label: '挑战者', value: this.frTotal},
        {label: '挑战者未入榜', value: this.fuTotal},
        {label: '新锐', value: this.rrTotal},
        {label: '新锐未入榜', value: this.ruTotal}
      ]
    },
    showEmpty () {
      if (this.pageLoading) {
        return false
      }
      return this.frTotal === 0 && this.fuTotal === 0 && this.rrTotal === 0 && this.ruTotal === 0
    }
  },
  mounted () {
    this.applyPeriod('month')
    this.loadStudyAdviserList()
    this.searchAll()
  },
  methods: {
    loadStudyAdviserList () {
      this.$http.post('/adminUser/listByCondition', {adminGroupId: 7, status: 1}).then((res) => {
        if (res && res.status === 'ok' && res.data) {
          this.studyAdviserList = res.data
        }
      })
    },
    applyPeriod (type) {
      const today = new Date()
      let start = new Date(today.getFullYear(), today.getMonth(), today.getDate())
      if (type === 'week') {
        const day = start.getDay() || 7
        start.setDate(start.getDate() - day + 1)
      } else if (type === 'month') {
        start = new Date(today.getFullYear(), today.getMonth(), 1)
      } else if (type === 'year') {
        start = new Date(today.getFullYear(), 0, 1)
      }
      const startText = type === 'all' ? ALL_START_DATE : formatDate(start)
      this.dateRange = [startText, formatDate(today)]
    },
    onPeriodTypeChange (type) {
      this.periodType = type
      this.applyPeriod(type)
      this.onFilterChange()
    },
    onDateRangeChange () {
      this.periodType = ''
      this.onFilterChange()
    },
    onFilterChange () {
      this.frPageIndex = 1
      this.fuPageIndex = 1
      this.rrPageIndex = 1
      this.ruPageIndex = 1
      this.$nextTick(() => {
        this.searchAll()
      })
    },
    onFrPageChange (page) {
      this.frPageIndex = page
      this.searchBlock('fr')
    },
    onFuPageChange (page) {
      this.fuPageIndex = page
      this.searchBlock('fu')
    },
    onRrPageChange (page) {
      this.rrPageIndex = page
      this.searchBlock('rr')
    },
    onRuPageChange (page) {
      this.ruPageIndex = page
      this.searchBlock('ru')
    },
    buildQuery (pageIndex, pageSize, board, listType) {
      const query = {
        startDate: toDateText(this.dateRange[0]),
        endDate: toDateText(this.dateRange[1]),
        pageIndex: pageIndex,
        pageSize: pageSize,
        listType: listType
      }
      if (board === 'rookie') {
        query.board = 'rookie'
      } else {
        query.board = 'formal'
        query.tier = 5
      }
      const adviserId = Number(this.studyAdviserId)
      if (Number.isInteger(adviserId) && adviserId > 0) {
        query.studyAdviserId = adviserId
      }
      const sex = Number(this.sex)
      if (sex === 1 || sex === 2) {
        query.sex = sex
      }
      if (this.openCourseRange && toDateText(this.openCourseRange[0]) && toDateText(this.openCourseRange[1])) {
        query.startOpenCourseDate = toDateText(this.openCourseRange[0])
        query.endOpenCourseDate = toDateText(this.openCourseRange[1])
      }
      return query
    },
    searchAll () {
      this.searchBlock('fr')
      this.searchBlock('fu')
      this.searchBlock('rr')
      this.searchBlock('ru')
    },
    blockMeta (key) {
      const map = {
        fr: {board: 'formal', listType: 'ranked', pageIndex: 'frPageIndex', pageSize: 'frPageSize', total: 'frTotal', data: 'frData', loading: 'frLoading', seq: 'frSeq', label: '挑战者'},
        fu: {board: 'formal', listType: 'unranked', pageIndex: 'fuPageIndex', pageSize: 'fuPageSize', total: 'fuTotal', data: 'fuData', loading: 'fuLoading', seq: 'fuSeq', label: '挑战者未入榜'},
        rr: {board: 'rookie', listType: 'ranked', pageIndex: 'rrPageIndex', pageSize: 'rrPageSize', total: 'rrTotal', data: 'rrData', loading: 'rrLoading', seq: 'rrSeq', label: '新锐榜'},
        ru: {board: 'rookie', listType: 'unranked', pageIndex: 'ruPageIndex', pageSize: 'ruPageSize', total: 'ruTotal', data: 'ruData', loading: 'ruLoading', seq: 'ruSeq', label: '新锐榜未入榜'}
      }
      return map[key]
    },
    searchBlock (key) {
      if (!this.dateRange || !toDateText(this.dateRange[0]) || !toDateText(this.dateRange[1])) {
        return
      }
      const meta = this.blockMeta(key)
      if (!meta) {
        return
      }
      const seq = ++this[meta.seq]
      const query = this.buildQuery(this[meta.pageIndex], this[meta.pageSize], meta.board, meta.listType)
      this[meta.loading] = true
      this.$http.post('/tch/hr-ranking', query).then((res) => {
        if (seq !== this[meta.seq]) {
          return
        }
        if (res && res.status === 'ok' && res.data) {
          const rows = res.data.data || []
          this[meta.data] = rows
          this[meta.total] = res.data.totalCount || 0
          if (key === 'fr') {
            this.expanded = {}
            if (this.frPageIndex === 1) {
              this.podiumCache = rows.filter(row => !isBlank(row.rank) && Number(row.rank) <= 3)
              this.champion = rows.find(row => Number(row.rank) === 1) || null
            }
          }
          if (key === 'rr') {
            this.rookieExpanded = {}
          }
        } else {
          this.resetBlock(key)
          this.$Message.error((res && res.msg) || (meta.label + '加载失败'))
        }
      }).catch(() => {
        if (seq !== this[meta.seq]) {
          return
        }
        this.resetBlock(key)
        this.$Message.error(meta.label + '加载失败')
      }).finally(() => {
        if (seq === this[meta.seq]) {
          this[meta.loading] = false
        }
      })
    },
    resetBlock (key) {
      const meta = this.blockMeta(key)
      if (!meta) {
        return
      }
      this[meta.data] = []
      this[meta.total] = 0
      if (key === 'fr') {
        this.champion = null
        this.podiumCache = []
        this.expanded = {}
      }
      if (key === 'rr') {
        this.rookieExpanded = {}
      }
    },
    toggle (tchId) {
      this.$set(this.expanded, tchId, !this.expanded[tchId])
    },
    toggleRookie (tchId) {
      this.$set(this.rookieExpanded, tchId, !this.rookieExpanded[tchId])
    },
    rookieRank (row, idx) {
      return isBlank(row.rank) ? idx + 1 : row.rank
    },
    unrankedReasonText (row) {
      if (!row) {
        return ''
      }
      const parts = []
      if (!isBlank(row.unrankedReason)) {
        parts.push(row.unrankedReason)
      }
      if (!isBlank(row.outReason)) {
        parts.push(row.outReason)
      }
      return parts.join('；')
    },
    isOutOnly (row) {
      return row && isBlank(row.unrankedReason) && !isBlank(row.outReason)
    },
    unrankedBadge (row) {
      return this.isOutOnly(row) ? '出榜' : '未入榜'
    },
    openTchInfo (row) {
      const id = Number(row && row.tchId)
      if (!Number.isInteger(id) || id <= 0) {
        return
      }
      this.tchId = id
      this.showTchInfoModal = true
    },
    initials (name) {
      if (isBlank(name)) {
        return '?'
      }
      const parts = String(name).trim().split(/\s+/)
      const text = parts.length > 1 ? parts[0][0] + parts[1][0] : parts[0].slice(0, 2)
      return text.toUpperCase()
    },
    medalClass (rank) {
      const r = Number(rank)
      if (r === 1) {
        return 'is-gold'
      }
      if (r === 2) {
        return 'is-silver'
      }
      if (r === 3) {
        return 'is-bronze'
      }
      return r <= 10 ? 'is-navy' : 'is-plain'
    },
    rankTitle (rank) {
      return {1: 'CHAMPION', 2: 'RUNNER-UP', 3: 'THIRD'}[rank] || ''
    },
    formatOpenCourse (val) {
      if (isBlank(val)) {
        return '—'
      }
      if (typeof val === 'number') {
        return formatDate(new Date(val))
      }
      return String(val).slice(0, 10)
    },
    formatScore (val) {
      if (isBlank(val)) {
        return '—'
      }
      const num = Number(val)
      return isNaN(num) ? String(val) : num.toFixed(2)
    },
    formatScoreShort (val) {
      const num = Number(val)
      if (isBlank(val) || isNaN(num)) {
        return '—'
      }
      return num % 1 === 0 ? String(num) : num.toFixed(1)
    },
    displayValue (val) {
      return isBlank(val) ? '—' : String(val)
    },
    gradeText (grade) {
      if (isBlank(grade)) {
        return '—'
      }
      return grade
    },
    gradeLevel (grade) {
      if (grade === 'A' || grade === 'B') {
        return 'is-healthy'
      }
      if (grade === 'C') {
        return 'is-warning'
      }
      if (grade === 'D' || grade === 'E' || grade === 'OUT') {
        return 'is-danger'
      }
      return ''
    },
    gradeWidth (grade) {
      return GRADE_WIDTH[grade] || '0%'
    },
    scoreLevel (score) {
      const num = Number(score)
      if (num >= 85) {
        return 'is-healthy'
      }
      if (num >= 70) {
        return 'is-warning'
      }
      return 'is-danger'
    },
    exportToImage () {
      if (this.frTotal === 0 && this.fuTotal === 0 && this.rrTotal === 0 && this.ruTotal === 0) {
        this.$Message.warning('暂无数据，无法导出')
        return
      }
      if (this.exportLoading) {
        return
      }
      this.exportLoading = true
      const start = toDateText(this.dateRange[0])
      const end = toDateText(this.dateRange[1])
      const el = this.$refs.exportArea
      const prevWidth = el.style.width
      el.style.width = '1120px'
      html2canvas(el, {
        scale: 2,
        backgroundColor: '#F5F6FA',
        useCORS: true,
        onclone: function (doc) {
          doc.querySelectorAll('.export-skip').forEach(function (node) {
            node.style.display = 'none'
          })
          doc.querySelectorAll('.podium-col, .rank-card, .hero').forEach(function (node) {
            node.style.animation = 'none'
            node.style.opacity = '1'
            node.style.transform = 'none'
          })
          const stage = doc.querySelector('.stage')
          if (stage) {
            stage.style.background = '#172440'
          }
        }
      }).then(canvas => {
        el.style.width = prevWidth
        const link = document.createElement('a')
        link.download = '优秀外教排行榜_' + start + '_' + end + '.png'
        link.href = canvas.toDataURL('image/png')
        link.click()
        this.exportLoading = false
        this.$Message.success('导出成功')
      }).catch(() => {
        el.style.width = prevWidth
        this.exportLoading = false
        this.$Message.error('导出失败')
      })
    },
    ringStyle (score) {
      const num = Math.max(0, Math.min(100, Number(score) || 0))
      const level = this.scoreLevel(score)
      const color = level === 'is-healthy' ? '#0B9E8D' : level === 'is-warning' ? '#E89F2D' : '#E6464E'
      return {background: 'conic-gradient(' + color + ' ' + (num * 3.6) + 'deg, #EEF0F5 0deg)'}
    },
    metricTip (row, metric) {
      const gradeLabel = (row[metric.grade] || '—') + ' 档'
      let tip = metric.title + '（权重 ' + metric.weight + '）：' + this.displayValue(row[metric.value]) +
        ' · ' + gradeLabel + ' · ' + this.formatScore(row[metric.score]) + ' 分'
      if (metric.extra && !isBlank(row[metric.extra])) {
        tip += ' · ' + metric.extraLabel + ' ' + row[metric.extra]
      }
      return tip
    }
  }
}
</script>

<style scoped lang="less">
@page-bg: #F5F6FA;
@header-bg: #2A3C62;
@header-bg-end: #314E78;
@stage-bg: #172440;
@text: #1A1A1A;
@text-title: #303133;
@text-muted: #8B93A7;
@text-desc: #9AA3B2;
@card: #FFFFFF;
@danger: #E6464E;
@warning: #E89F2D;
@healthy: #0B9E8D;
@gold-a: #FFE08A;
@gold-b: #E3A21A;
@silver-a: #F1F4F8;
@silver-b: #A3ADBD;
@bronze-a: #F2BE8C;
@bronze-b: #B26B34;
@rookie: #1F9D6B;
@rookie-line: #B9D9C6;
@rookie-bg: #F3FBF6;
@radius-header: 16px;
@radius-card: 12px;
@gap: 16px;
@shadow: 0 1px 2px rgba(15, 23, 42, .04), 0 8px 24px rgba(15, 23, 42, .04);

.tch-ranking-page {
  position: relative;
  margin: -24px;
  min-height: calc(100vh - 108px);
  padding: 16px;
  background: @page-bg;
}

/* hero */
.hero {
  position: relative;
  overflow: hidden;
  padding: 24px 28px;
  border-radius: @radius-header;
  background: linear-gradient(110deg, @header-bg 0%, @header-bg-end 100%);
  color: #fff;
}

.hero-glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(40px);
  pointer-events: none;
}

.hero-glow-a {
  top: -80px;
  right: 18%;
  width: 260px;
  height: 260px;
  background: rgba(255, 207, 92, .28);
}

.hero-glow-b {
  bottom: -120px;
  left: -40px;
  width: 280px;
  height: 280px;
  background: rgba(11, 158, 141, .25);
}

.hero-main {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 20px;
}

.hero-kicker {
  font-size: 11px;
  letter-spacing: 3px;
  color: rgba(255, 224, 138, .85);
}

.hero-title {
  margin: 6px 0 0;
  font-size: 26px;
  font-weight: 800;
  letter-spacing: 1px;
  color: #fff;
}

.hero-sub {
  margin: 6px 0 0;
  font-size: 12px;
  color: rgba(255, 255, 255, .7);
}

.hero-sub-keep {
  white-space: nowrap;
}

.hero-stats {
  display: flex;
  gap: 12px;
}

.hero-stat {
  min-width: 108px;
  padding: 12px 16px;
  border: 1px solid rgba(255, 255, 255, .12);
  border-radius: 12px;
  background: rgba(255, 255, 255, .08);
  backdrop-filter: blur(4px);
  text-align: center;
}

.hero-stat-value {
  max-width: 160px;
  overflow: hidden;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.3;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: #fff;
}

.hero-stat-label {
  margin-top: 2px;
  font-size: 12px;
  color: rgba(255, 255, 255, .6);
}

/* toolbar */
.rank-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px 20px;
  margin-top: @gap;
  padding: 12px 16px;
  border-radius: @radius-card;
  background: @card;
  box-shadow: @shadow;
}

.seg {
  display: inline-flex;
  padding: 3px;
  border-radius: 10px;
  background: #EEF1F6;
}

.seg-item {
  padding: 5px 16px;
  border-radius: 8px;
  font-size: 13px;
  color: @text-muted;
  transition: all .2s;

  &:hover {
    color: @header-bg;
  }

  &.is-active {
    background: #fff;
    font-weight: 600;
    color: @header-bg;
    box-shadow: 0 2px 6px rgba(42, 60, 98, .15);
  }
}

.tool-field {
  display: inline-flex;
  align-items: center;
}

.tool-label {
  margin-right: 8px;
  font-size: 13px;
  color: @text-title;
}

.range-picker {
  width: 220px;
}

.adviser-select {
  width: 160px;
}

.sex-select {
  width: 160px;
}

.tool-actions {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 16px;
}

.rule-link {
  font-size: 13px;
  color: @header-bg-end;
}

.rule-link.is-busy {
  opacity: .6;
  pointer-events: none;
}

/* stage & podium */
.stage {
  position: relative;
  overflow: hidden;
  margin-top: @gap;
  padding: 24px 24px 0;
  border-radius: @radius-header;
  background: radial-gradient(ellipse at 50% 0%, #2B4170 0%, @stage-bg 70%);
}

.stage-beam {
  position: absolute;
  top: -40px;
  width: 220px;
  height: 520px;
  background: linear-gradient(180deg, rgba(255, 236, 170, .22) 0%, rgba(255, 236, 170, 0) 75%);
  clip-path: polygon(42% 0, 58% 0, 100% 100%, 0 100%);
  pointer-events: none;
}

.stage-beam-l {
  left: 14%;
  transform: rotate(14deg);
}

.stage-beam-c {
  left: 50%;
  margin-left: -110px;
}

.stage-beam-r {
  right: 14%;
  transform: rotate(-14deg);
}

.stage-title {
  position: relative;
  text-align: center;
  font-size: 12px;
  letter-spacing: 4px;
  color: rgba(255, 224, 138, .9);
}

.podium {
  position: relative;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 18px;
  margin-top: 10px;
}

.podium-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 260px;
  animation: rise .6s ease-out both;
}

.podium-person {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 0 8px 14px;
}

.crown {
  font-size: 30px;
  line-height: 1;
  color: @gold-b;
  text-shadow: 0 0 14px rgba(255, 210, 90, .8);
  animation: float 2.4s ease-in-out infinite;
}

.avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  margin-top: 4px;
  border-radius: 50%;
  padding: 3px;

  span {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background: #22355C;
    font-size: 22px;
    font-weight: 700;
    color: #fff;
  }

  &.is-gold {
    background: linear-gradient(135deg, @gold-a, @gold-b);
    box-shadow: 0 0 26px rgba(255, 200, 70, .55);
  }

  &.is-silver {
    background: linear-gradient(135deg, @silver-a, @silver-b);
    box-shadow: 0 0 18px rgba(220, 228, 240, .35);
  }

  &.is-bronze {
    background: linear-gradient(135deg, @bronze-a, @bronze-b);
    box-shadow: 0 0 18px rgba(230, 160, 100, .35);
  }
}

.is-rank-1 .avatar {
  width: 92px;
  height: 92px;

  span {
    font-size: 28px;
  }
}

.podium-name {
  display: block;
  max-width: 100%;
  margin-top: 12px;
  overflow: hidden;
  font-size: 16px;
  font-weight: 700;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: #fff;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.podium-id {
  margin-top: 2px;
  font-size: 12px;
  color: rgba(255, 255, 255, .55);
}

.podium-score {
  margin-top: 6px;
  font-size: 30px;
  font-weight: 800;
  line-height: 1.1;
  color: #fff;
}

.is-rank-1 .podium-score {
  font-size: 38px;
  background: linear-gradient(180deg, #FFF3C4, @gold-b);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.podium-score-unit {
  margin-left: 2px;
  font-size: 13px;
  font-weight: 400;
  -webkit-text-fill-color: rgba(255, 255, 255, .6);
}

.podium-bars {
  width: 100%;
  margin-top: 10px;
}

.pbar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  font-size: 12px;
  color: rgba(255, 255, 255, .7);
}

.pbar-label {
  width: 48px;
  text-align: right;
}

.pbar-track {
  flex: 1;
  height: 6px;
  overflow: hidden;
  border-radius: 3px;
  background: rgba(255, 255, 255, .12);
}

.pbar-fill {
  display: block;
  height: 100%;
  border-radius: 3px;
  background: @text-desc;

  &.is-healthy {
    background: linear-gradient(90deg, #12C2AC, @healthy);
  }

  &.is-warning {
    background: linear-gradient(90deg, #F6C46A, @warning);
  }

  &.is-danger {
    background: linear-gradient(90deg, #F68A8F, @danger);
  }
}

.pbar-value {
  width: 58px;
  color: #fff;
}

.pedestal {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  padding-top: 10px;
  border-radius: 12px 12px 0 0;
  background: linear-gradient(180deg, rgba(255, 255, 255, .16), rgba(255, 255, 255, .04));
  border-top: 3px solid;
}

.pedestal-num {
  font-size: 40px;
  font-weight: 900;
  line-height: 1;
  color: rgba(255, 255, 255, .9);
}

.pedestal-tag {
  margin-top: 4px;
  font-size: 11px;
  letter-spacing: 3px;
  color: rgba(255, 255, 255, .55);
}

.is-rank-1 .pedestal {
  height: 140px;
  border-top-color: @gold-b;
}

.is-rank-2 .pedestal {
  height: 108px;
  border-top-color: @silver-b;
}

.is-rank-3 .pedestal {
  height: 84px;
  border-top-color: @bronze-b;
}

/* medal */
.medal {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
}

.medal-core {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  font-weight: 800;
  color: #fff;
  box-shadow: inset 0 0 0 2px rgba(255, 255, 255, .45), 0 3px 8px rgba(0, 0, 0, .18);
}

.medal-ribbon {
  position: absolute;
  bottom: -10px;
  width: 10px;
  height: 18px;
  background: @header-bg;
  clip-path: polygon(0 0, 100% 0, 100% 100%, 50% 75%, 0 100%);
}

.medal-ribbon-l {
  left: 22%;
  transform: rotate(18deg);
  background: #E6464E;
}

.medal-ribbon-r {
  right: 22%;
  transform: rotate(-18deg);
  background: #2F6BD8;
}

.medal-podium {
  width: 34px;
  height: 34px;
  margin-top: -16px;
  font-size: 16px;
}

.medal-sm {
  width: 40px;
  height: 40px;
  font-size: 16px;
}

.medal.is-gold .medal-core {
  background: radial-gradient(circle at 30% 30%, @gold-a, @gold-b 70%);
}

.medal.is-silver .medal-core {
  background: radial-gradient(circle at 30% 30%, @silver-a, @silver-b 70%);
  color: #4A5468;
}

.medal.is-bronze .medal-core {
  background: radial-gradient(circle at 30% 30%, @bronze-a, @bronze-b 70%);
}

.medal.is-navy .medal-core {
  background: radial-gradient(circle at 30% 30%, #4E6FA8, @header-bg 70%);
}

.medal.is-plain {
  .medal-core {
    background: #F1F3F8;
    color: @text-title;
    box-shadow: inset 0 0 0 2px #DDE2EC;
  }

  .medal-ribbon {
    display: none;
  }
}

.block-empty-hint {
  margin-top: 6px;
  font-size: 12px;
  color: #999;
  font-weight: normal;
}
.block-empty {
  margin: 0 0 16px;
  padding: 18px 16px;
  color: #8a94a6;
  font-size: 13px;
  text-align: center;
  background: rgba(255, 255, 255, 0.55);
  border: 1px dashed rgba(138, 148, 166, 0.45);
  border-radius: 12px;
}

/* board */
.board-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin: 22px 0 12px;
}

.board-title {
  margin: 0;
  padding-left: 10px;
  border-left: 4px solid @header-bg;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.2;
  color: @text;

  &.is-danger {
    border-left-color: @danger;
  }
  &.is-warn {
    border-left-color: #fa8c16;
    color: #d48806;
  }

  &.is-rookie {
    border-left-color: @rookie;
    color: @rookie;
  }
}

.rookie-box {
  margin-top: 22px;
  padding: 0 16px 16px;
  border: 1.5px dashed @rookie-line;
  border-radius: @radius-header;
  background: @rookie-bg;

  .board-head {
    margin-top: 16px;
  }
}

.medal-rookie .medal-core {
  background: radial-gradient(circle at 30% 30%, #5FD3A0, @rookie 70%);
  font-size: 12px;
}

.rank-avatar.is-rookie {
  background: linear-gradient(135deg, #5FD3A0, @rookie);
}

.rookie-tag {
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 8px;
  background: rgba(31, 157, 107, .12);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1px;
  vertical-align: 2px;
  color: @rookie;
}

.board-legend {
  font-size: 12px;
  color: @text-muted;
}

.legend-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin: 0 4px 0 8px;
  border-radius: 50%;
  vertical-align: 1px;

  &.is-healthy {
    background: @healthy;
  }

  &.is-warning {
    background: @warning;
  }

  &.is-danger {
    background: @danger;
  }
}

.card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  align-items: start;
  gap: @gap;
}

.rank-card {
  position: relative;
  padding: 16px 16px 12px 20px;
  overflow: hidden;
  border-radius: @radius-card;
  background: @card;
  box-shadow: @shadow;
  transition: transform .2s, box-shadow .2s;
  animation: rise .45s ease-out both;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 4px;
    background: @text-desc;
  }

  &.is-healthy::before {
    background: @healthy;
  }

  &.is-warning::before {
    background: @warning;
  }

  &.is-danger::before,
  &.is-out::before {
    background: @danger;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 2px 4px rgba(15, 23, 42, .05), 0 14px 32px rgba(15, 23, 42, .1);
  }

  &.is-out {
    background: linear-gradient(180deg, #FFF6F6, #fff 60%);
  }
}

.rank-card-top {
  display: flex;
  align-items: center;
  gap: 14px;
}

.rank-avatar {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 2px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4E6FA8, @header-bg);
  text-decoration: none;
  cursor: pointer;

  span {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background: #22355C;
    font-size: 13px;
    font-weight: 700;
    color: #fff;
  }

  &:hover {
    box-shadow: 0 0 0 3px rgba(42, 60, 98, .18);
  }
}

.rank-card-who {
  flex: 1;
  min-width: 0;
}

.rank-card-name {
  display: block;
  overflow: hidden;
  font-size: 15px;
  font-weight: 700;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: @text;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.top10 {
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 8px;
  background: rgba(42, 60, 98, .08);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1px;
  vertical-align: 2px;
  color: @header-bg;
}

.rank-card-sub {
  margin-top: 2px;
  overflow: hidden;
  font-size: 12px;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: @text-muted;
}

.score-ring {
  flex: none;
  width: 64px;
  height: 64px;
  padding: 5px;
  border-radius: 50%;
}

.score-ring-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: #fff;
}

.score-ring-num {
  font-size: 17px;
  font-weight: 800;
  line-height: 1;
  color: @text;
}

.score-ring-unit {
  margin-top: 2px;
  font-size: 10px;
  color: @text-muted;
}

.strip {
  display: flex;
  gap: 3px;
  height: 8px;
  margin-top: 14px;
}

.strip-seg {
  border-radius: 2px;
  background: #E3E7EE;
  cursor: help;
  transition: transform .15s;

  &:hover {
    transform: scaleY(1.6);
  }

  &.is-healthy {
    background: @healthy;
  }

  &.is-warning {
    background: @warning;
  }

  &.is-danger {
    background: @danger;
  }
}

.strip-caption {
  display: flex;
  justify-content: space-between;
  margin-top: 6px;
  font-size: 11px;
  color: @text-desc;
}

.toggle {
  color: @header-bg-end;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  margin-top: 10px;
}

.metric-chip {
  padding: 8px 10px;
  border-radius: 8px;
  background: #F7F8FB;
}

.metric-chip-label {
  font-size: 11px;
  color: @text-muted;

  em {
    margin-left: 4px;
    font-style: normal;
    color: @text-desc;
  }
}

.metric-chip-value {
  margin-top: 2px;
  font-size: 14px;
  font-weight: 600;
  color: @text;
}

.metric-chip-grade {
  font-size: 11px;
  color: @text-desc;

  &.is-healthy {
    color: @healthy;
  }

  &.is-warning {
    color: @warning;
  }

  &.is-danger {
    color: @danger;
  }
}

.out-badge {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: 2px dashed @danger;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 700;
  color: @danger;
  transform: rotate(-12deg);
}
.out-badge.is-unranked {
  border-color: #fa8c16;
  color: #fa8c16;
  background: rgba(250, 140, 22, 0.08);
}


.out-reason {
  margin-top: 4px;
  font-size: 12px;
  font-weight: 600;
  color: @danger;
}

.out-score {
  flex: none;
  text-align: center;
}

.out-score-num {
  font-size: 18px;
  font-weight: 700;
  color: @text-desc;
  text-decoration: line-through;
}

.out-score-label {
  font-size: 11px;
  color: @text-desc;
}

.empty {
  margin-top: @gap;
  padding: 56px 0;
  border-radius: @radius-card;
  background: @card;
  box-shadow: @shadow;
  text-align: center;
}

.empty-trophy {
  font-size: 44px;
  filter: grayscale(.6);
  opacity: .7;
}

.empty-title {
  margin-top: 8px;
  font-size: 15px;
  font-weight: 600;
  color: @text-title;
}

.empty-sub {
  margin-top: 4px;
  font-size: 12px;
  color: @text-muted;
}

.pager {
  margin-top: 20px;
  text-align: center;
}

.rule-head {
  margin: 0 0 12px;
  font-size: 13px;
  color: @text-title;
}

.rule-title {
  margin: 16px 0 6px;
  padding-left: 8px;
  border-left: 3px solid @header-bg;
  font-size: 14px;
  font-weight: 600;
  color: @text-title;

  &.is-rookie {
    margin-top: 20px;
    border-left-color: @rookie;
  }
}

.rule-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.rule-table th,
.rule-table td {
  padding: 8px 10px;
  border: 1px solid #E8EAEC;
  text-align: center;
}

.rule-table th {
  background: #F8F8F9;
  color: @text-title;
}

.rule-foot {
  margin: 12px 0 0;
  font-size: 12px;
  line-height: 1.6;
  color: @text-muted;
}

@keyframes rise {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
}
</style>



// WEBPACK FOOTER //
// TchTeacherRanking.vue?aa00ec90